import { useCallback, useEffect, useMemo, useState } from 'react';
import { dbFetchMapsByLabel, dbUpdateMap, dbFetchMapById, dbFetchAllMaps } from '@/api/maps';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MapData } from './types/MapData';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/stores/notificationStore';

export function computeConsonantSkeleton(name: string): string {
    if (!name) return "";
    const clean = name.toLowerCase().trim();
    let result = "";
    
    for (let i = 0; i < clean.length; i++) {
        const char = clean[i];
        
        if (/[aeiouyhאהעי]/.test(char)) {
            continue;
        }
        
        if (/[bvwfבופף]/.test(char)) {
            result += 'b';
        } else if (/[kcqכךק]/.test(char)) {
            result += 'k';
        } else if (/[tטת]/.test(char)) {
            result += 't';
        } else if (/[dד]/.test(char)) {
            result += 'd';
        } else if (/[szxזסצש]/.test(char)) {
            result += 's';
        } else if (/[gג]/.test(char)) {
            result += 'g';
        } else if (/[lל]/.test(char)) {
            result += 'l';
        } else if (/[mםמ]/.test(char)) {
            result += 'm';
        } else if (/[nןנ]/.test(char)) {
            result += 'n';
        } else if (/[rר]/.test(char)) {
            result += 'r';
        } else if (/[jז]/.test(char)) {
            result += 'j';
        } else {
            if (/[a-z]/.test(char)) {
                result += char;
            }
        }
    }
    return result;
}

/**
 * Synchronously retrieves and parses cached myMap from localStorage.
 */
function getCachedMyMap(userId?: string): MapData | null {
    if (typeof window === 'undefined' || !userId) return null;
    try {
        const item = localStorage.getItem(`spirit_my_map_${userId}`);
        if (item) {
            return JSON.parse(item) as MapData;
        }
    } catch (e) {
        console.warn('[useMyMap] Failed to parse cached myMap:', e);
    }
    return null;
}

/**
 * Persists or removes cached myMap in localStorage.
 */
function setCachedMyMap(userId?: string, map?: MapData | null) {
    if (typeof window === 'undefined' || !userId) return;
    try {
        if (map) {
            localStorage.setItem(`spirit_my_map_${userId}`, JSON.stringify(map));
        } else {
            localStorage.removeItem(`spirit_my_map_${userId}`);
        }
    } catch (e) {
        console.warn('[useMyMap] Failed to save cached myMap:', e);
    }
}

export const useMyMap = () => {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [localError, setLocalError] = useState<string | null>(null);

    const cachedInitial = useMemo(() => getCachedMyMap(user?.id), [user?.id]);

    const myMapQuery = useQuery({
        queryKey: ['myMap', user?.id],
        queryFn: async () => {
            if (!user) return null;
            const data = await dbFetchMapsByLabel(user.id, 'אני');
            const found = (data && data.length > 0) ? data[0] : null;
            setCachedMyMap(user.id, found);
            return found;
        },
        initialData: cachedInitial ?? undefined,
        enabled: !!user?.id,
        staleTime: 1000 * 60 * 5, // 5 minutes cache
    });

    const myMap = myMapQuery.data ?? null;
    const loading = myMapQuery.isLoading;
    const error = localError || (myMapQuery.error ? (myMapQuery.error as Error).message : null);

    const fetchMyMap = useCallback(async () => {
        if (!user) return;
        setLocalError(null);
        await myMapQuery.refetch();
    }, [user, myMapQuery]);

    const findSuggestedMaps = useCallback(async (name: string): Promise<MapData[]> => {
        if (!user || !name.trim()) return [];

        const inputName = name.trim().toLowerCase();
        const nameParts = name.trim().split(/\s+/).map(p => p.toLowerCase());
        if (nameParts.length === 0) return [];

        try {
            // Fetch all user maps to perform in-memory matching cascade
            const allMaps = await dbFetchAllMaps(user.id);
            if (!allMaps || allMaps.length === 0) return [];

            const userEmailPrefix = user.email ? user.email.split('@')[0].toLowerCase() : '';

            const p1Maps: MapData[] = []; // Strict Exact Same-Language Match
            const p2Maps: MapData[] = []; // Bilingual Consonant Skeleton Match
            const p3Maps: MapData[] = []; // Email Prefix Match
            const p4Maps: MapData[] = []; // Partial Same-Language Substring Match

            const inputSkeleton = computeConsonantSkeleton(inputName);
            const emailPrefixSkeleton = userEmailPrefix ? computeConsonantSkeleton(userEmailPrefix) : '';

            allMaps.forEach(map => {
                const mapFirstName = (map.firstName || '').toLowerCase().trim();
                const mapFamilyName = (map.familyName || '').toLowerCase().trim();
                const mapFullName = `${mapFirstName} ${mapFamilyName}`.trim();
                
                const mapFirstNameSkeleton = computeConsonantSkeleton(mapFirstName);
                const mapFullNameSkeleton = computeConsonantSkeleton(mapFullName);

                // Priority 1: Strict Exact Same-Language Name Match
                if (mapFirstName === inputName || mapFullName === inputName) {
                    p1Maps.push(map);
                    return;
                }

                // Priority 2: Bilingual Consonant Skeleton Match
                if (inputSkeleton && (mapFirstNameSkeleton === inputSkeleton || mapFullNameSkeleton === inputSkeleton)) {
                    p2Maps.push(map);
                    return;
                }

                // Priority 3: Email Prefix Match
                if (userEmailPrefix && (mapFirstName === userEmailPrefix || mapFullName === userEmailPrefix || (emailPrefixSkeleton && mapFirstNameSkeleton === emailPrefixSkeleton))) {
                    p3Maps.push(map);
                    return;
                }

                // Priority 4: Partial Same-Language Substring Match
                const hasPartial = nameParts.some(part => {
                    return (mapFirstName.includes(part) || mapFamilyName.includes(part));
                });
                if (hasPartial) {
                    p4Maps.push(map);
                    return;
                }
            });

            // Combine arrays preserving strict priority order
            const combinedMatches = [...p1Maps, ...p2Maps, ...p3Maps, ...p4Maps];

            // De-duplicate results by map ID
            const seenIds = new Set<string>();
            const uniqueMatches: MapData[] = [];

            combinedMatches.forEach(map => {
                if (!seenIds.has(map.id)) {
                    seenIds.add(map.id);
                    uniqueMatches.push(map);
                }
            });

            // Return top 5 matches
            return uniqueMatches.slice(0, 5);

        } catch (err: unknown) {
            console.error('Error finding suggested maps:', err);
            return [];
        }
    }, [user]);

    const setAsMe = useCallback(async (mapId: string) => {
        if (!user) return;
        setLocalError(null);
        
        // Optimistic UI updates
        // 1. Snapshot previous myMap and maps list
        const previousMyMap = queryClient.getQueryData<MapData | null>(['myMap', user.id]);
        const previousMaps = queryClient.getQueryData<MapData[]>(['maps', user.id]);

        // 2. Locate target map to extract current details
        let targetMap = previousMaps?.find(m => m.id === mapId);
        if (!targetMap) {
            try {
                targetMap = await dbFetchMapById(mapId) || undefined;
            } catch (e) {
                console.error("Failed to fetch target map during setAsMe:", e);
            }
        }
        if (!targetMap) {
            setLocalError('Map not found');
            throw new Error('Map not found');
        }

        if (targetMap.labels?.includes('אני')) {
            queryClient.setQueryData(['myMap', user.id], targetMap);
            return targetMap;
        }

        const updatedTargetMap = {
            ...targetMap,
            labels: [...(targetMap.labels || []).filter(l => l !== 'אני'), 'אני']
        };

        // 3. Set the optimistic state for 'myMap' query
        queryClient.setQueryData(['myMap', user.id], updatedTargetMap);
        setCachedMyMap(user.id, updatedTargetMap);

        // 4. Set the optimistic state for 'maps' list query
        if (previousMaps) {
            queryClient.setQueryData(['maps', user.id], (old: MapData[] | undefined) => {
                if (!old) return [];
                return old.map(m => {
                    if (m.id === mapId) {
                        return updatedTargetMap;
                    }
                    if (m.labels?.includes('אני')) {
                        return {
                            ...m,
                            labels: m.labels.filter(l => l !== 'אני')
                        };
                    }
                    return m;
                });
            });
        }

        // 5. Run DB updates quietly in the background
        const runBackgroundUpdates = async () => {
            try {
                // Fetch other maps that might have 'אני' label to remove it
                const currentMeMaps = await dbFetchMapsByLabel(user.id, 'אני');
                if (currentMeMaps?.length) {
                    const updates = currentMeMaps
                        .filter(m => m.id !== mapId)
                        .map(m => {
                            const newLabels = (m.labels || []).filter((l: string) => l !== 'אני');
                            return dbUpdateMap(m.id, { labels: newLabels });
                        });
                    await Promise.all(updates);
                }

                // Add 'אני' to target map
                await dbUpdateMap(mapId, { labels: updatedTargetMap.labels });

                // Quiet validations and refetch
                queryClient.invalidateQueries({ queryKey: ['myMap', user.id] });
                queryClient.invalidateQueries({ queryKey: ['maps', user.id] });
            } catch (err) {
                console.error("Background setAsMe failed, rolling back:", err);
                setLocalError(err instanceof Error ? err.message : 'An unknown error occurred');
                queryClient.setQueryData(['myMap', user.id], previousMyMap);
                setCachedMyMap(user.id, previousMyMap);
                toast.error("שגיאה בעדכון המפה האישית בשרת. הנתונים שוחזרו.");
                throw err;
            }
        };

        // Execution awaits DB synchronization so subsequent requests pass self-map gates
        await runBackgroundUpdates();

        return updatedTargetMap;
    }, [user, queryClient]);

    useEffect(() => {
        if (!user) {
            queryClient.setQueryData(['myMap', null], null);
        }
    }, [user, queryClient]);

    return {
        myMap,
        loading,
        error,
        fetchMyMap,
        setAsMe,
        findSuggestedMaps
    };
};
